import type { Commitment } from "design-scope";
import type {
  DesignScopeListItem,
  DesignScopePackageLink
} from "mock-data/design-scope";
import type { DesignScopeUpdateInstance } from "services/design-scope-apis";
import { compareStringsAlphabetically } from "utils/utils";
import {
  lodForOptionId,
  type LevelOfDetailOption
} from "../hooks/use-level-of-detail";
import { toBulkLinkDesignPackageUpdateInstance } from "./design-scope-create-mappers";
import { toModalScopeItem } from "./design-scope-list-mappers";
import { scopeTitle } from "./scope-list-cells";

/** Local (unsaved) LOD + note for one scope item on the milestone. */
export type MilestoneLinkDraft = { lodId: string; notes: string };

export type MilestoneLinkDrafts = Record<string, MilestoneLinkDraft>;

/** The item's link row for this design package milestone, if any. */
export function milestoneLinkOf(
  item: DesignScopeListItem,
  designPackageId: string,
  milestoneId: string
): DesignScopePackageLink | undefined {
  return item.design_packages.find(
    (link) =>
      link.design_package?.id === designPackageId &&
      link.design_package_milestone?.id === milestoneId
  );
}

/** Saved LOD + note for every item already linked to the milestone. */
export function initialMilestoneLinks(
  items: DesignScopeListItem[],
  designPackageId: string,
  milestoneId: string
): MilestoneLinkDrafts {
  const out: MilestoneLinkDrafts = {};
  for (const item of items) {
    const link = milestoneLinkOf(item, designPackageId, milestoneId);
    if (!link) continue;
    out[item.id] = {
      lodId:
        link.level_of_detail?.id == null ? "" : String(link.level_of_detail.id),
      notes: link.notes ?? ""
    };
  }
  return out;
}

/**
 * Manage Links order: linked items first, then the displayed title A-Z ignoring
 * case, with blank titles last in each group. The id tie-break keeps duplicate
 * titles from swapping when the API returns them in a different order.
 *
 * Pass the links saved when the picker opened, not the live selection, so rows
 * stay put while the user toggles checkboxes.
 */
export function sortScopeItemsForManageLinks(
  items: DesignScopeListItem[],
  linkedIds: ReadonlySet<string>
): DesignScopeListItem[] {
  return items
    .map((item) => ({ item, title: scopeTitle(item).trim() }))
    .sort(
      (a, b) =>
        Number(linkedIds.has(b.item.id)) - Number(linkedIds.has(a.item.id)) ||
        Number(!a.title) - Number(!b.title) ||
        compareStringsAlphabetically(a.title, b.title) ||
        compareStringsAlphabetically(a.item.id, b.item.id)
    )
    .map(({ item }) => item);
}

/** Draft LOD, falling back to the project default when none is chosen yet. */
export function effectiveLodId(
  draft: MilestoneLinkDraft | undefined,
  defaultLodId: string
): string {
  return draft?.lodId || defaultLodId;
}

function sameDraft(
  a: MilestoneLinkDraft,
  b: MilestoneLinkDraft,
  defaultLodId: string
): boolean {
  return (
    effectiveLodId(a, defaultLodId) === effectiveLodId(b, defaultLodId) &&
    a.notes.trim() === b.notes.trim()
  );
}

export type MilestoneLinkChanges = {
  linked: string[];
  unlinked: string[];
  edited: string[];
};

/** Which items get linked, unlinked, or have their LOD / note edited. */
export function diffMilestoneLinks(
  initial: MilestoneLinkDrafts,
  selected: ReadonlySet<string>,
  drafts: MilestoneLinkDrafts,
  defaultLodId: string
): MilestoneLinkChanges {
  const changes: MilestoneLinkChanges = {
    linked: [],
    unlinked: [],
    edited: []
  };
  for (const id of Object.keys(initial)) {
    if (!selected.has(id)) {
      changes.unlinked.push(id);
      continue;
    }
    const draft = drafts[id];
    if (draft && !sameDraft(draft, initial[id]!, defaultLodId)) {
      changes.edited.push(id);
    }
  }
  for (const id of selected) {
    if (!initial[id]) changes.linked.push(id);
  }
  return changes;
}

/**
 * One `/features/update` row per changed item. The update API replaces an
 * item's whole design/bid package lists, so each row carries the item's other
 * commitments and bid packages unchanged and only swaps this milestone's link.
 */
export function buildMilestoneLinkUpdates(params: {
  items: DesignScopeListItem[];
  changes: MilestoneLinkChanges;
  drafts: MilestoneLinkDrafts;
  designPackageId: string;
  milestoneId: string;
  lodOptions: LevelOfDetailOption[];
  defaultLodId: string;
}): DesignScopeUpdateInstance[] {
  const {
    items,
    changes,
    drafts,
    designPackageId,
    milestoneId,
    lodOptions,
    defaultLodId
  } = params;
  const byId = new Map(items.map((item) => [item.id, item]));
  const linkIds = new Set([...changes.linked, ...changes.edited]);
  const ids = [...changes.linked, ...changes.edited, ...changes.unlinked];

  const out: DesignScopeUpdateInstance[] = [];
  for (const id of ids) {
    const item = byId.get(id);
    if (!item) continue;
    const scope = toModalScopeItem(item);
    const commitments: Commitment[] = scope.commitments.filter(
      (c) =>
        !(
          c.designPackageId === designPackageId && c.milestoneId === milestoneId
        )
    );
    if (linkIds.has(id)) {
      const lodId = effectiveLodId(drafts[id], defaultLodId);
      commitments.push({
        designPackageId,
        milestoneId,
        lod: lodForOptionId(lodOptions, lodId),
        levelOfDetailValueId: lodId,
        notes: drafts[id]?.notes ?? ""
      });
    }
    out.push(
      toBulkLinkDesignPackageUpdateInstance(id, commitments, scope.bids)
    );
  }
  return out;
}
